import { useEffect, useRef } from 'react'
import { paidInPerson } from '../lib/storePayment'
import { useStoreOwner } from '../store/useStoreOwner'
import { useStoreOrders } from '../hooks/useStoreOrders'
import { useI18n } from '../i18n/useI18n'
import { playChime, NEW_ORDER_TONES } from '../lib/chime'
import { playPaymentSound } from '../lib/paymentSounds'
import { showStoreEventBanner } from './StoreEventBanner'

/**
 * #968 店舗オーナー向けグローバル通知のマウント。オーナーがログインしている間、
 * App ルートに常設され、所有する各店舗の注文を購読して以下を「どの画面にいても」
 * 通知音＋バナーで知らせる:
 *   - 新規注文（received / 承認待ち）→ 注文チャイム
 *   - 現金会計リクエスト（cashPending false→true）→ 注文チャイム
 *   - カード決済完了（paid false→true, 非現金）→ 決済音(#959)
 * これまで StoreOrders 画面の中だけで鳴っていた notifier をグローバル化したもの。
 * 二重発火を避けるため、StoreOrders からは新規注文/決済音 notifier を撤去している。
 *
 * **店長・店員 (店員ログイン) の端末でも鳴らす** (#2140 で発覚)。以前はオーナーの Firebase
 * ログインのときだけ置かれていて、現場の端末 (厨房・店頭のタブレット) は店員ログインが
 * 普通なのに、新しい注文が来ても音もお知らせも出なかった。店員ログインのときは、
 * そのセッションの店 (`staffStoreId`) を見る。
 */
export default function StoreNotifierMount({ staffStoreId = null }: { staffStoreId?: string | null }) {
  const isOwner = useStoreOwner((s) => s.isStoreLoggedIn)
  const ownedStores = useStoreOwner((s) => s.ownedStores)
  const fetchOwnedStores = useStoreOwner((s) => s.fetchOwnedStores)
  const volume = useStoreOwner((s) => s.storeSoundVolume)
  const paymentSoundId = useStoreOwner((s) => s.paymentSoundId)

  // ログイン直後など ownedStores が未取得なら一度だけ取りに行く (オーナーのときだけ。
  // 店員ログインにはオーナーのトークンが無いので取れない)。
  useEffect(() => {
    if (isOwner && ownedStores.length === 0) {
      fetchOwnedStores().catch(() => { /* 通知は best-effort。失敗しても無視 */ })
    }
  }, [isOwner, ownedStores.length, fetchOwnedStores])

  const stores = (isOwner ? ownedStores : []).map((s) => s as { id?: string; name?: string })
  // 店員ログインの店。オーナーとしても見ている店なら二重に鳴らさない
  if (staffStoreId && !stores.some((s) => s.id === staffStoreId)) stores.push({ id: staffStoreId, name: '' })

  return (
    <>
      {stores.map((store) => {
        if (!store.id) return null
        return (
          <StoreEventWatcher
            key={store.id}
            storeId={store.id}
            storeName={store.name || ''}
            volume={volume}
            paymentSoundId={paymentSoundId}
          />
        )
      })}
    </>
  )
}

interface WatcherProps {
  storeId: string
  storeName: string
  volume: number
  paymentSoundId: string
}

function StoreEventWatcher({ storeId, storeName, volume, paymentSoundId }: WatcherProps) {
  const { t } = useI18n()
  const { orders, loading } = useStoreOrders(storeId)
  // orderId → 直近に見た {paid, cashPending}。null = 次スナップショットで要(再)seed。
  const prevRef = useRef<Map<string, { paid: boolean; cashPending: boolean }> | null>(null)

  useEffect(() => {
    if (loading) {
      prevRef.current = null
      return
    }
    // 初回スナップショット = 既存バックログ。無音で seed。
    if (prevRef.current === null) {
      const seed = new Map<string, { paid: boolean; cashPending: boolean }>()
      for (const o of orders) seed.set(o.id, { paid: !!o.paid, cashPending: !!o.cashPending })
      prevRef.current = seed
      return
    }

    const prev = prevRef.current
    // 通知音 ON/OFF は端末ごと（StoreOrders のトグルと共有）。既定 ON。
    const soundOn = (() => {
      try {
        return localStorage.getItem(`storeOrders.soundEnabled.${storeId}`) !== 'false'
      } catch {
        return true
      }
    })()

    let newOrder: typeof orders[number] | null = null
    let cashReq: typeof orders[number] | null = null
    let cardPaid: typeof orders[number] | null = null
    let paidSettled = false  // #959 決済音用: 現金/カード問わず paid 遷移があったか。

    for (const o of orders) {
      const before = prev.get(o.id)
      if (o.orderType !== 'delivery') {
        if (!before) {
          // 新規に現れた注文（dine-in）。受付/承認待ちなら新規注文として通知。
          if (o.status === 'received' || o.status === 'pending_approval') newOrder = o
        } else {
          // 現金会計リクエスト: cashPending false→true（店員が集金に行く合図）。
          if (!before.cashPending && o.cashPending) cashReq = o
          // 決済完了: paid false→true。決済音(#959)は現金/カード両方で鳴らす。
          // バナーはカード（非現金）のみ — 現金は既にリクエスト時に通知済みのため。
          if (!before.paid && o.paid) {
            paidSettled = true
            // #2162 お店の決済端末で受けた会計もレジで店員が受けたものなので、現金と同じく出さない
            if (!paidInPerson(o.paymentMethod)) cardPaid = o
          }
        }
      }
      prev.set(o.id, { paid: !!o.paid, cashPending: !!o.cashPending })
    }

    // スナップショットから消えた id を破棄（シフト中のメモリ肥大を防ぐ）。
    for (const id of prev.keys()) {
      if (!orders.some((o) => o.id === id)) prev.delete(id)
    }

    const tableLabel = (o: typeof orders[number]) =>
      o.tableNumber ? t('storeOrders.notifyTable', { table: o.tableNumber }) : ''
    const body = (o: typeof orders[number], extra?: string) =>
      [storeName, tableLabel(o), extra].filter(Boolean).join(' ・ ')

    if (newOrder) {
      if (soundOn) playChime(NEW_ORDER_TONES, volume)
      showStoreEventBanner({ type: 'new', storeId, title: t('storeOrders.notifyNewOrderTitle'), body: body(newOrder) })
    }
    if (cashReq) {
      if (soundOn) playChime(NEW_ORDER_TONES, volume)
      showStoreEventBanner({ type: 'cash', storeId, title: t('storeOrders.notifyCashTitle'), body: body(cashReq) })
    }
    // 決済音(#959) はオーナー設定。soundId '' のときは playPaymentSound 側で無音。
    // 現金/カードどちらの会計完了でも鳴らす（#959 の挙動を維持）。
    if (paidSettled) playPaymentSound(paymentSoundId)
    if (cardPaid) {
      const amount = `¥${(cardPaid.totalAmount || 0).toLocaleString()}`
      showStoreEventBanner({ type: 'card', storeId, title: t('storeOrders.notifyCardTitle'), body: body(cardPaid, amount) })
    }
  }, [orders, loading, volume, paymentSoundId, storeId, storeName, t])

  return null
}
